<template>
  <div
    class="card2"
    :style="{
      backgroundImage: 'url(' + pageCardTheme.backgroundImage + ')',
      backgroundColor: pageCardTheme.backgroundColor2,
      color: pageCardTheme.color,
    }"
  >
    <div class="card2-gradient">
      <div class="card2-head">
        <div class="card2-left">
          <div class="card2-info-wrap">
            <div
              class="card2-logo"
              :style="{
                backgroundImage: 'url(' + cardInfo.corpLogo + ')',
              }"
            ></div>
            <div class="card2-name">
              {{ cardInfo.name }}
              <img v-if="globalStore.isManagerView" class="card2-edit" @click="clickEditBtn" :src="HEAD_EDIT_ICON" />
              <div v-if="cardInfo.position" class="card2-position text-ellipsis">{{ cardInfo.position }}</div>
            </div>
            <div v-if="cardInfo.corpName" class="card2-info text-ellipsis-line-2">{{ cardInfo.corpName }}</div>
            <template v-if="cardInfo.deptList && cardInfo.deptList.length > 0">
              <div v-for="item in cardInfo.deptList" :key="item" class="card2-info text-ellipsis-line-2">
                {{ item }}
              </div>
            </template>
          </div>

          <template v-if="cardInfo.tagList && cardInfo.tagList.length">
            <div
              class="card2-tags"
              :style="{
                color: pageCardTheme.tagFontColor,
              }"
            >
              <span
                v-for="(item, index) in cardInfo.tagList"
                :key="index"
                class="card2-tag"
                :style="{
                  backgroundColor: pageCardTheme.tagColor,
                }"
              >
                {{ item }}
              </span>
            </div>
          </template>
        </div>
        <div class="card2-right">
          <div
            class="card2-avatar"
            :style="{
              backgroundImage: 'url(' + cardInfo.avatar + ')',
            }"
            @click="clickAvatar"
          >
            <template v-if="cardInfo.certificateList && cardInfo.certificateList.length">
              <img class="card2-avatar-authentication" :src="AuthImg2" />
              <div class="card2-avatar-authentication-click" @click="toggleHandle"></div>
            </template>
          </div>
          <div
            class="card2-contact"
            :style="{
              color: iconColor,
            }"
          >
            <BaseIconSvg
              v-if="cardInfo.contact && userStore.viewStaffInfo?.qrCode"
              type="czmingpian-weixin"
              @click="clickWechat"
              size="0.56rem"
            />
            <BaseIconSvg v-if="cardInfo.mobile" size="0.56rem" type="czmingpian-dianhua" @click="clickMobile" />
          </div>
        </div>
      </div>
      <div>
        <template v-if="cardInfo.desc">
          <div class="card2-item">
            <span class="cz-iconfont czliangdian card2-item-icon"></span>
            <div class="card2-item-text">
              <p v-for="(item, index) in cardInfo.desc.split('\n')" :key="index">
                {{ item }}
              </p>
            </div>
          </div>
        </template>

        <template
          v-if="
            !state.toggle &&
            (cardInfo.address ||
              (cardInfo.certificateList && cardInfo.certificateList.length) ||
              hasLocalizationCertificate)
          "
        >
          <span
            class="cz-iconfont czmingpian-gengduo card2-toggle-down"
            :style="{
              color: iconColor,
            }"
            @click="toggleHandle"
          ></span>
        </template>
      </div>

      <template v-if="state.toggle">
        <div class="card2-foot">
          <template v-if="cardInfo.address">
            <div class="card2-item">
              <span class="cz-iconfont czdizhi card2-item-icon"></span>
              <div class="card2-item-text">{{ cardInfo.address }}</div>
            </div>
          </template>

          <template v-if="cardInfo.certificateList && cardInfo.certificateList.length">
            <CertificateBanner :certificateList="cardInfo.certificateList" />
          </template>

          <SPDBLocalizationCertificate v-if="hasLocalizationCertificate" />

          <span
            class="cz-iconfont czmingpian-gengduo-shouqi card2-toggle-up"
            :style="{
              color: iconColor,
            }"
            @click="toggleHandle"
          ></span>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
  import AuthImg2 from '@shared/assets/images/managerCard/authentication_2.png';
  import { BaseIconSvg } from '@shared/components';
  import { HEAD_EDIT_ICON } from '@shared/config';
  import { useBusinessCardStore, useGlobalStore, useUserStore } from '@shared/store';
  import { storeToRefs } from 'pinia';
  import { computed, reactive } from 'vue';
  import CertificateBanner from '../certificate-banner/index.vue';
  import SPDBLocalizationCertificate from '../spdb-localization-certificate/index.vue';
  import { useWechat, useMobile, useAvatar, useCard, useSPDBLocalizationCertificate } from '../../composables';

  const globalStore = useGlobalStore();
  const userStore = useUserStore();
  const { clickWechat } = useWechat();
  const { clickMobile } = useMobile();
  const { clickAvatar } = useAvatar();
  const { clickEditBtn } = useCard();
  const { hasLocalizationCertificate } = useSPDBLocalizationCertificate();

  const state = reactive({ toggle: false });

  const cardStore = useBusinessCardStore();

  const { pageCardTheme, pageCardInfo: cardInfo } = storeToRefs(cardStore);

  const iconColor = computed(() => pageCardTheme.value.iconColor);

  const toggleHandle = (e: Event) => {
    e.stopPropagation();
    state.toggle = !state.toggle;
  };
</script>

<style lang="less" scoped>
  .card2 {
    overflow: hidden;
    background-repeat: no-repeat;
    background-position: right bottom;
    background-size: 100%;
    border-radius: 0.1rem;
    box-shadow: 0 0.1rem 0.2rem 0 rgba(0, 0, 0, 0.15);

    &-gradient {
      padding: 0.2rem 0.3rem 0.1rem;
      overflow: hidden;
      background: ~"url('../../assets/images/managerCard/gradient.png')" no-repeat;
      background-size: 100% 100%;
    }

    &-head {
      display: flex;
      align-items: stretch;
      justify-content: space-between;
    }

    &-left {
      width: 4.8rem;
    }

    &-logo {
      height: 0.32rem;
      background-repeat: no-repeat;
      background-position: left 0;
      background-size: contain;
    }

    &-name {
      display: flex;
      align-items: center;
      line-height: 0.8rem;
      font-size: 0.36rem;
      font-weight: bold;
      white-space: nowrap;
    }

    &-edit {
      width: 0.5rem;
      margin-left: 0.12rem;
    }

    &-position {
      line-height: 0.7rem;
      margin-left: 0.08rem;
      font-size: 0.22rem;
      font-weight: normal;
    }

    &-info {
      line-height: 0.3rem;
      font-size: 0.22rem;

      &-wrap {
        margin-bottom: 0.2rem;
      }
    }

    &-tags {
      display: flex;
      flex-wrap: wrap;
      overflow: hidden;
    }

    &-tag {
      height: 0.4rem;
      line-height: 0.4rem;
      padding: 0 0.2rem;
      margin: 0 0.1rem 0.1rem 0;
      overflow: hidden;
      font-size: 0.24rem;
      white-space: nowrap;
      border-radius: 0.24rem;
    }

    &-right {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      width: 1.4rem;
      min-height: 2.55rem;
      max-height: 3.05rem;
      padding: 0.1rem 0;
    }

    &-avatar {
      position: relative;
      width: 1.4rem;
      height: 1.4rem;
      background-color: #fff;
      background-repeat: no-repeat;
      background-position: center center;
      background-size: cover;
      border-radius: 50%;
      // border: 0.02rem solid #fff;
      box-shadow: 0 0.1rem 0.15rem 0 rgba(0, 0, 0, 0.15);

      &-authentication {
        position: absolute;
        top: -0.08rem;
        left: -0.19rem;
        width: 1.78rem;
      }

      &-authentication-click {
        position: absolute;
        bottom: -0.22rem;
        left: -0.19rem;
        z-index: 10;
        width: 1.78rem;
        height: 0.6rem;
      }
    }

    &-contact {
      display: flex;
      flex-direction: row-reverse;
      align-items: center;
      justify-content: space-between;
      width: 1.4rem;

      &-icon {
        font-size: 0.56rem;
      }
    }

    &-toggle-up,
    &-toggle-down {
      padding: 0.16rem 0.3rem;
      margin-left: 2.7rem;
      opacity: 0.2;
    }

    &-foot {
      width: 100%;
    }

    &-item {
      display: flex;
      line-height: 0.3rem;
      padding: 0.1rem 0;
      font-size: 0.22rem;
    }

    &-item-icon {
      margin-right: 0.15rem;
      font-size: 0.26rem;
    }
  }
</style>
